/**
 * Mega-menu catalog for the Client TopBar.
 *
 * Built on top of the existing `NAV_REGISTRY` from nav-registry.ts so we have
 * a single source of truth for routes. Each top-level NavItem in the registry
 * becomes one mega-menu group; its children become tiles. Top-level items
 * without children are grouped under "Accès rapide".
 *
 * Copilot sections are excluded from the mega-menu (they live in the Copilot
 * sidebar / chat panel) so the launcher stays focused on operational pages.
 */

import { Compass, type LucideIcon } from 'lucide-react'

import { NAV_REGISTRY, type NavItem } from '@/config/nav-registry'
import type { ContextId } from '@/types/real-estate'
import type { MegaMenuGroup, MegaMenuTile } from '@/components/layout/MegaMenuGrid'

// ─── Group title → accent color ────────────────────────────────────────────
//
// Heuristic mapping by keyword in the title. Falls back to slate.
function pickAccent(title: string): MegaMenuGroup['accent'] {
  const t = title.toLowerCase()
  if (t.includes('risque')) return 'orange'
  if (t.includes('conformité') || t.includes('compliance')) return 'teal'
  if (t.includes('sécurité') || t.includes('security')) return 'red'
  if (t.includes('donnée') || t.includes('data')) return 'purple'
  if (t.includes('ia') || t.includes('ai ')) return 'violet'
  if (t.includes('audit')) return 'cyan'
  if (t.includes('gouvernance') || t.includes('governance')) return 'teal'
  if (t.includes('évaluation') || t.includes('maturité')) return 'indigo'
  if (t.includes('stratégie') || t.includes('strategy') || t.includes('vision')) return 'amber'
  if (t.includes('éthique') || t.includes('ethics')) return 'green'
  if (t.includes('vendor') || t.includes('fournisseur') || t.includes('tprm')) return 'blue'
  if (t.includes('change') || t.includes('formation')) return 'green'
  if (t.includes('dashboard') || t.includes('rapport')) return 'amber'
  if (t.includes('workspace')) return 'slate'
  if (t.includes('framework') || t.includes('référentiel')) return 'indigo'
  if (t.includes('preuve') || t.includes('evidence')) return 'cyan'
  if (t.includes('copilot') || t.includes('ia copilot')) return 'pink'
  return 'slate'
}

const MAX_TILES_PER_GROUP = 9

function flattenItemToTiles(item: NavItem): MegaMenuTile[] {
  if (item.href) {
    return [
      {
        label: item.title,
        icon: item.icon as LucideIcon,
        href: item.href,
      },
    ]
  }
  if (item.children && item.children.length > 0) {
    const out: MegaMenuTile[] = []
    for (const child of item.children) {
      if (child.href) {
        out.push({
          label: child.title,
          icon: child.icon as LucideIcon,
          href: child.href,
        })
      } else if (child.children) {
        for (const grand of child.children) {
          if (grand.href) {
            out.push({
              label: grand.title,
              icon: grand.icon as LucideIcon,
              href: grand.href,
            })
          }
        }
      }
    }
    return out
  }
  return []
}

function topLevelItemToGroup(item: NavItem, idx: number): MegaMenuGroup | null {
  // Skip Copilot sections — they belong to the assistant panel, not the launcher.
  const isCopilot = (item.title ?? '').toLowerCase().includes('copilot')
  if (isCopilot) return null

  const tiles = flattenItemToTiles(item).slice(0, MAX_TILES_PER_GROUP)
  if (tiles.length === 0) return null

  return {
    id: `${item.title}-${idx}`,
    label: item.title,
    icon: item.icon as LucideIcon,
    accent: pickAccent(item.title),
    tiles,
  }
}

/**
 * Build the mega-menu groups for a given persona context.
 * - Pulls from the existing NAV_REGISTRY.
 * - Groups any orphan items (no children, just an href) into "Accès rapide".
 * - Caps at ~6 groups so the popover fits a normal viewport.
 */
export function getMegaMenuGroupsForContext(contextId: ContextId): MegaMenuGroup[] {
  const items = NAV_REGISTRY[contextId] ?? []
  const groups: MegaMenuGroup[] = []
  const quickAccess: MegaMenuTile[] = []

  for (let i = 0; i < items.length; i++) {
    const item = items[i]
    if (item.href && (!item.children || item.children.length === 0)) {
      // Orphan link — collect for "Accès rapide" group
      quickAccess.push({
        label: item.title,
        icon: item.icon as LucideIcon,
        href: item.href,
      })
      continue
    }
    const g = topLevelItemToGroup(item, i)
    if (g) groups.push(g)
    if (groups.length >= 6) break
  }

  if (quickAccess.length > 0) {
    groups.unshift({
      id: 'quick-access',
      label: 'Accès rapide',
      icon: Compass,
      accent: 'slate',
      tiles: quickAccess.slice(0, MAX_TILES_PER_GROUP),
    })
  }

  return groups
}
